<template>
  <section class="grow overflow-y-auto">
    <ul class="flex flex-col gap-y-2">
      <!-- Innovadis custom: inline catalog first-level categories -->
      <li v-if="catalogMenuItems">
        <ul class="flex flex-col gap-y-2">
          <li v-for="cat in catalogMenuItems" :key="cat.id || cat.title">
            <InnoMobileMenuLink :link="cat" class="py-1 font-bold" @click="$emit('selectItem', cat)">
              <div class="flex items-center">
                <div class="bg-white mr-4 rounded-lg">
                  <VcImage :src="cat.imgSrc" class="size-12 p-1" />
                </div>
                <span class="font-semibold">{{ cat.title }}</span>
              </div>
            </InnoMobileMenuLink>
          </li>
        </ul>
      </li>
    </ul>

    <ul class="divide-y divide-additional-50 divide-opacity-20 mt-4">
      <li v-for="item in mainMenuItems" :key="item.title" class="py-1">
        <InnoMobileMenuLink
          :icon="item.route === '/nieuw' ? 'new' : item.route === '/acties' ? 'promotion' : undefined"
          :link="item"
          class="py-1 font-semibold"
          @click="$emit('close')"
        >
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-1" v-if="mobileAccountMenuItem && isAuthenticated">
        <InnoMobileMenuLink
          icon="custom-user"
          :link="mobileAccountMenuItem"
          @select="$emit('selectItem', mobileAccountMenuItem!)"
        >
          {{ mobileAccountMenuItem.title }}
        </InnoMobileMenuLink>
      </li>

      <li v-if="!isAuthenticated" v-for="item in unauthorizedMenuItems" :key="item.title">
        <InnoMobileMenuLink :key="item.title" :link="item" class="py-3" @close="$emit('close')">
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-3" v-if="loginOnBehalf">
        <router-link
          :to="{ name: 'SignInAdministrator' }"
          @click="$emit('close')"
          class="flex items-center justify-between"
        >
          {{ $t("shared.account.sign_in_form.impersonate.switch_user") }}

          <VcIcon :size="16" class="ml-auto fill-[--mobile-menu-navigation-color]" name="chevron-right-small" />
        </router-link>
      </li>

      <li v-for="item in topHeaderList" :key="item.title" class="py-1">
        <InnoMobileMenuLink
          :icon="item.route === '/nieuw' ? 'new' : item.route === '/acties' ? 'promotion' : undefined"
          :link="item"
          class="py-1"
          @click="$emit('close')"
        >
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-3" v-if="isAuthenticated">
        <div @click="signMeOut" class="flex items-center justify-between">
          <VcIcon :size="20" class="mr-3 fill-[--mobile-menu-navigation-color]" name="sign-out" />

          {{ $t("shared.layout.header.link_logout") }}

          <VcIcon :size="16" class="ml-auto fill-[--mobile-menu-navigation-color]" name="chevron-right-small" />
        </div>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useNavigations } from "@/core/composables";
import { useSignMeOut, useUser } from "@/shared/account";
import type { ExtendedMenuLinkType } from "@/core/types";
import InnoMobileMenuLink from "@/shared/layout/components/header/_internal/mobile-menu/inno-mobile-menu-link.vue";
import { computedEager } from "@vueuse/core";
import { VirtoPermissions, CustomPermissions } from "@/core/enums";

interface IProps {
  menuItem: ExtendedMenuLinkType;
}

interface IEmits {
  (event: "close"): void;
  (event: "selectItem", item: ExtendedMenuLinkType): void;
}

defineEmits<IEmits>();
defineProps<IProps>();

const { signMeOut } = useSignMeOut();
const { checkPermissions, isMultiOrganization, isAuthenticated } = useUser();
const { topHeaderList, catalogMenuItems, mainMenuItems, mobileMarketingMenuItem } = useNavigations();
const { t } = useI18n();

const loginOnBehalf = computedEager<boolean>(() => checkPermissions(VirtoPermissions.LoginOnBehalf));
// Innovadis custom: only expose Dashboard in mobile account navigation when Power BI is available.
const canViewPowerBiDashboard = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowPowerBI));
// Innovadis custom: only expose the company link when company information is actually available.
const canViewCompanyInfo = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowViewCompanyInformation));
// Innovadis custom: only expose Lists in mobile account navigation when wishlist access exists.
const canViewWishlists = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowWishlist));
const showOrderHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowOrderHistory));
const showInvoiceHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowInvoiceHistory));
const showUserManagement = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowUserManagement));

const unauthorizedMenuItems: ExtendedMenuLinkType[] = [
  { route: { name: "SignIn" }, title: t("shared.layout.header.link_sign_in") },
  { route: { name: "SignUp" }, title: t("shared.layout.header.link_register_now") },
];

const mobileAccountMenuItem = computed<ExtendedMenuLinkType>(() => {
  const dashboardMenuItem = canViewPowerBiDashboard.value
    ? {
        route: { name: "Dashboard" },
        title: t("shared.layout.header.mobile.account_menu.dashboard"),
        icon: "dashboard-2",
      }
    : {
        route: { name: "Profile" },
        title: t("shared.layout.header.mobile.account_menu.profile"),
        icon: "profile",
      };

  return {
    id: "account",
    title: t("shared.layout.header.mobile.my_account"),
    icon: "user-circle",
    children: [
      dashboardMenuItem,
      ...(canViewPowerBiDashboard.value
        ? [
            {
              route: { name: "Profile" },
              title: t("shared.layout.header.mobile.account_menu.profile"),
              icon: "profile",
            },
          ]
        : []),
      ...(isMultiOrganization.value
        ? [
            {
              route: { name: "DropOffPoint" },
              title: t("shared.account.navigation.links.drop_off_point"),
              icon: "truck",
            },
          ]
        : []),
      {
        route: { name: "ChangePasswordAccount" },
        title: t("shared.layout.header.mobile.account_menu.change_password"),
        icon: "pass",
      },
      // Innovadis custom: place notifications under the account section so mobile users find them after change password.
      {
        id: "push-messages",
        route: { name: "Notifications" },
        title: t("push_messages.menu_item_name"),
        icon: "custom-notifications",
      },
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Orders" },
              title: t("shared.layout.header.mobile.account_menu.orders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Backorders" },
              title: t("shared.layout.header.mobile.account_menu.backorders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showInvoiceHistory.value
        ? [
            {
              route: { name: "Invoices" },
              title: t("shared.layout.header.mobile.account_menu.invoices"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(canViewWishlists.value
        ? [
            {
              route: { name: "Lists" },
              title: t("shared.layout.header.mobile.account_menu.lists"),
              icon: "list-v2",
            },
          ]
        : []),
      ...(canViewCompanyInfo.value
        ? [
            {
              route: { name: "CompanyInfo" },
              title: t("shared.layout.header.mobile.corporate_menu.company_info"),
              icon: "office-building",
            },
          ]
        : []),
      ...(showUserManagement.value
        ? [
            {
              route: { name: "CompanyMembers" },
              title: t("shared.layout.header.mobile.corporate_menu.company_members"),
              icon: "user-group",
            },
          ]
        : []),
    ],
  };
});
</script>
